import React from 'react';
import { Calendar, TrendingUp, Clock, Star, ChevronRight, Flame, Droplets, Wind, Mountain, Sparkles } from 'lucide-react';
import { HOROSCOPE_TRANSLATIONS } from '../../../../languages/horoscope';

type Period = 'daily' | 'weekly' | 'monthly' | 'yearly';

interface SidebarControlsProps {
    period: Period;
    onPeriodChange: (period: Period) => void;
    selectedSign: {
        name: string;
        image: string;
        dates: string;
        element: string;
    };
    language: string;
}

const SidebarControls: React.FC<SidebarControlsProps> = ({ period, onPeriodChange, selectedSign, language }) => {
    const langKey = language === 'hindi' ? 'Hindi' : 'English';
    const t = HOROSCOPE_TRANSLATIONS[langKey];

    const periodTabs = [
        { id: 'daily', label: t.daily, icon: Calendar },
        { id: 'weekly', label: t.weekly, icon: TrendingUp },
        { id: 'monthly', label: t.monthly, icon: Clock },
        { id: 'yearly', label: t.yearly, icon: Star },
    ];

    const getElementIcon = (element: string) => {
        switch (element) {
            case 'Fire': return <Flame size={14} className="text-orange-400" />;
            case 'Water': return <Droplets size={14} className="text-blue-400" />;
            case 'Air': return <Wind size={14} className="text-sky-200" />;
            case 'Earth': return <Mountain size={14} className="text-emerald-400" />;
            default: return <Sparkles size={14} className="text-purple-400" />;
        }
    };

    return (
        <div className="lg:col-span-3 space-y-6">
            {/* Period Selector */}
            <div className="bg-white/[0.08] rounded-2xl lg:rounded-3xl p-1.5 lg:p-2 border border-white/20">
                <div className="grid grid-cols-4 lg:grid-cols-1 gap-1">
                    {periodTabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => onPeriodChange(tab.id as Period)}
                            className={`flex flex-col lg:flex-row items-center justify-center lg:justify-start gap-1 lg:gap-3 px-2 py-2 lg:px-4 lg:py-3 rounded-xl transition-all duration-300 font-medium text-[10px] sm:text-xs lg:text-sm font-sans group ${period === tab.id
                                ? 'bg-purple-600/20 text-purple-200 border border-purple-500/20 shadow-inner'
                                : 'text-purple-300/60 hover:text-white hover:bg-white/5'
                                }`}
                        >
                            <div className={`p-1.5 rounded-full transition-colors ${period === tab.id ? 'bg-purple-500/20' : 'bg-transparent group-hover:bg-white/5'}`}>
                                <tab.icon size={16} className={`lg:w-[18px] lg:h-[18px] ${period === tab.id ? 'text-purple-200' : 'text-current'}`} />
                            </div>
                            <span className="capitalize">{tab.label}</span>
                            {period === tab.id && <ChevronRight size={14} className="ml-auto opacity-50 hidden lg:block" />}
                        </button>
                    ))}
                </div>
            </div>

            {/* Selected Sign Info Card */}
            <div className="bg-white/[0.08] rounded-3xl p-6 border border-purple-500/10 text-center">
                <div className="w-24 h-24 mx-auto mb-4 relative group">
                    <div className="absolute inset-0 bg-purple-500/20 rounded-full blur-xl group-hover:blur-2xl transition-colors" />
                    <img
                        src={selectedSign.image}
                        alt={selectedSign.name}
                        className="w-full h-full object-contain relative z-10"
                    />
                </div>
                <h2 className="text-2xl font-black text-white mb-1 font-sans">{t[selectedSign.name] || selectedSign.name}</h2>
                <div className="flex items-center justify-center gap-2 mb-4">
                    <div className="flex items-center gap-1 text-xs font-bold text-purple-300 bg-purple-500/10 px-2 py-1 rounded-lg font-sans">
                        {getElementIcon(selectedSign.element)}
                        {t[selectedSign.element] || selectedSign.element}
                    </div>
                </div>
                <p className="text-sm text-purple-200/50 mb-4 px-2 font-sans font-light italic">
                    {selectedSign.dates}
                </p>
            </div>
        </div>
    );
};

export default SidebarControls;
